/**
 * L'inventaire des outils de l'espace connecté — source de vérité unique.
 *
 * Il a été écrit deux fois, et les deux copies ont divergé : le hub listait le
 * Sonomètre, la barre ne le listait pas ; les libellés ne concordaient pas ;
 * l'ordre non plus. Trois surfaces lisent désormais ce seul fichier — la grille
 * de `/app`, le panneau de la pastille de commutation, et le menu du compte en
 * mobile. Toute nouvelle surface qui liste les outils doit lire ici.
 *
 * Ne vivent ici que les routes de l'espace connecté. Les listes de l'`AppBar`
 * publique, du pied de page et de `HomeSemaine` pointent délibérément vers les
 * pages d'atterrissage SEO (`/generateur`, `/cahier-journal`, …), qui sont
 * d'autres pages : ce ne sont pas des doublons de celle-ci.
 *
 * Les `key` sont exactement celles que le backend renvoie dans les compteurs de
 * `/dashboard` et dans l'instrumentation `hub_tool_click`. **Renommer une clé
 * casse les compteurs en silence.**
 *
 * Le regroupement en quatre moments est celui de `HomeSemaine.svelte`, où le
 * produit raconte ces huit outils par les quatre moments d'une semaine de
 * classe. Il ne structure aujourd'hui aucune surface de l'espace connecté — il
 * ne sert plus qu'à fixer l'ordre de lecture, du dimanche soir à la fin de
 * période.
 */

import {
	TrendingUp,
	FileText,
	CalendarDays,
	Volume2,
	Armchair,
	Wand2,
	ClipboardCheck,
	GraduationCap,
	Hourglass,
	Route,
	Presentation
} from 'lucide-svelte';
import { ISSR_VISIBLE } from '$lib/issr';

/** Toutes les icônes lucide partagent la même signature ; l'une sert de patron. */
type IconeLucide = typeof FileText;

export interface Outil {
	/** Clé d'instrumentation, alignée sur `hub_tool_click` et `DashboardData.counts`. */
	key: string;
	href: string;
	/** Le libellé unique — barre, feuille mobile, hub et titre de page. */
	label: string;
	/**
	 * Une ligne de description — la grille de `/app` et le panneau de la pastille
	 * l'affichent l'une comme l'autre. C'est la copie produit, pas un raccourci
	 * de navigation.
	 */
	detail: string;
	icon: IconeLucide;
	match: (p: string) => boolean;
}

export interface Moment {
	id: string;
	/**
	 * Le libellé de nav. C'est la seule chose à changer pour renommer un moment
	 * partout — onglet, languette du sélecteur, en-tête de groupe de la feuille.
	 * Court : il doit tenir dans un onglet, et les quatre doivent tenir sur une
	 * ligne avec leurs outils au-delà de 1024 px.
	 */
	court: string;
	/** Le titre de récit de la home, gardé pour les surfaces qui respirent. */
	recit: string;
	/** Une ligne, à droite du rang d'outils sur les grands écrans. */
	sous: string;
	/** Les deux variables d'intercalaire de la papeterie sourde. */
	tab: string;
	outils: Outil[];
}

export const MOMENTS: Moment[] = [
	{
		id: 'preparer',
		court: 'Préparer',
		recit: 'Préparer sa matière',
		sous: 'La matière avant la semaine.',
		tab: '--tab: var(--color-buvard); --tab-deep: var(--color-buvard-deep)',
		outils: [
			{
				key: 'progressions',
				href: '/app/progressions',
				label: 'Progressions annuelles',
				detail: "Votre plan d'année par matière, réparti sur les 5 périodes.",
				icon: TrendingUp,
				match: (p: string) => p.startsWith('/app/progressions')
			},
			{
				key: 'sequences',
				href: '/app/sequences',
				label: 'Fiches séquences',
				detail: 'Des séquences complètes alignées sur les programmes en vigueur.',
				icon: FileText,
				match: (p: string) => p.startsWith('/app/sequences')
			}
		]
	},
	{
		// « Mon année » : ce qui se tient au fil de l'année plutôt que d'une
		// préparation — le cahier journal jour par jour, et les 108 heures qui se
		// comptent de septembre à août — et le suivi ISSR des remplaçants, tant
		// que le barème est en ligne (ISSR_VISIBLE).
		id: 'annee',
		court: 'Mon année',
		recit: "L'année se remplit",
		sous: 'Le cahier journal, jour par jour, et les heures hors classe.',
		tab: '--tab: var(--color-vert-cahier); --tab-deep: var(--color-vert-deep)',
		outils: [
			{
				key: 'journal',
				href: '/journal',
				label: 'Cahier journal',
				detail: 'Votre semaine jour par jour, générée et modifiable.',
				icon: CalendarDays,
				match: (p: string) => p === '/journal' || p.startsWith('/journal/')
			},
			{
				key: 'heures_108',
				href: '/app/108-heures',
				label: '108 heures',
				detail: "Vos heures hors classe, comptées et prêtes pour l'IEN.",
				icon: Hourglass,
				match: (p: string) => p.startsWith('/app/108-heures')
			},
			...(ISSR_VISIBLE
				? [
						{
							key: 'issr',
							href: '/app/issr',
							label: 'Suivi ISSR',
							detail: 'Vos remplacements et l’indemnité attendue, mois par mois.',
							icon: Route,
							match: (p: string) => p.startsWith('/app/issr')
						}
					]
				: [])
		]
	},
	{
		id: 'classe',
		court: 'En classe',
		recit: 'Pendant que ça vit',
		sous: 'Ce qui sert pendant la classe.',
		tab: '--tab: var(--color-kraft); --tab-deep: var(--color-kraft-deep)',
		outils: [
			{
				key: 'mode_classe',
				href: '/app/classe?source=outils',
				label: 'Ma journée au tableau',
				detail: 'Le programme du jour projeté, avec minuteur, sonomètre et pauses.',
				icon: Presentation,
				match: (p: string) => p === '/app/classe' || p.startsWith('/app/classe/')
			},
			{
				key: 'plan_de_classe',
				href: '/app/plan-de-classe',
				label: 'Plan de classe',
				detail: 'Placez vos élèves automatiquement, imprimez le plan.',
				icon: Armchair,
				match: (p: string) => p.startsWith('/app/plan-de-classe')
			},
			{
				key: 'fiches_exercices',
				href: '/app/coloriage',
				label: "Fiches d'exercices",
				detail: 'Des exercices à imprimer sur le thème de votre classe.',
				icon: Wand2,
				match: (p: string) => p.startsWith('/app/coloriage')
			},
			{
				key: 'sonometre',
				href: '/sonometre',
				label: 'Sonomètre',
				detail: 'Rendez le niveau sonore visible pour la classe.',
				icon: Volume2,
				match: (p: string) => p === '/sonometre' || p.startsWith('/sonometre/')
			}
		]
	},
	{
		id: 'boucler',
		court: 'Boucler',
		recit: 'Boucler sans y laisser ses soirées',
		sous: 'La période se referme.',
		tab: '--tab: var(--color-gomme); --tab-deep: var(--color-gomme-deep)',
		outils: [
			{
				key: 'evaluations',
				href: '/app/evaluations',
				label: 'Évaluations',
				detail: 'Des sujets différenciés, corrigés inclus.',
				icon: ClipboardCheck,
				match: (p: string) => p.startsWith('/app/evaluations')
			},
			{
				key: 'bulletins',
				href: '/generateur-bulletin',
				label: 'Appréciations de bulletin',
				detail: 'Trois propositions personnalisées par élève.',
				icon: GraduationCap,
				match: (p: string) => p === '/generateur-bulletin' || p.startsWith('/generateur-bulletin/')
			}
		]
	}
];

/** Tous les outils à plat, dans l'ordre de la semaine. */
export const OUTILS: Outil[] = MOMENTS.flatMap((m) => m.outils);

/** Le moment auquel appartient une route, ou `null` hors des outils (ex. `/app`). */
export function momentDe(path: string): Moment | null {
	return MOMENTS.find((m) => m.outils.some((o) => o.match(path))) ?? null;
}

/** L'outil courant, ou `null` sur le hub `/app`. */
export function outilDe(path: string): Outil | null {
	return OUTILS.find((o) => o.match(path)) ?? null;
}
